/**
 * THE ROOT.
 *
 * `/` is Home, through the entry, which asks the Worker who is signed in and shows the door, the
 * page that says to call, or Home. Every place a signed-in person reaches is routed the same way,
 * `<Entry screen={...} />`, so each one is behind the same question: Schedule at `/schedule`,
 * Payments at `/payments`, an invoice's own page at `/invoices/:invoiceId`, Messages at
 * `/messages`, Feedback on one visit at `/feedback/:visitId`, and one visit's report at
 * `/visits/:visitId` (home #21), each path written once in `paths.ts`. The page Stripe sends her
 * back to, `/payments/return`, notes what the address says before it asks the same question
 * (`screens/PaymentReturn.tsx`). Account, the page about her that
 * the header's Account button leads to, is at `/account`, with How you pay at `/account/paying`
 * and People and sign-in at `/account/people` (slice A1, built in full by slice A3), Your
 * agreement at `/account/agreement`, which Account's regular cleans lead to (round five, slice C2),
 * and What we send you at `/account/sending`, once ops has pushed her choices (ops's section 11.3,
 * P114).
 * `/sign-in` is the door's Sign in: two steps, the email address and then the emailed code, kept in
 * that screen's state rather than in the path, and it comes back to `/` once the Worker has set the
 * session, handing Home the page a signed-out visitor came for, which Home sends her on to once her
 * household has come down (`lib/cameFor.ts`, P107). Every other path lands on `/`, because the asset server answers an unknown path with
 * index.html, and a client who followed an old or mistyped link should reach the way in rather than
 * a blank page.
 *
 * After every move she makes from one place to another, the focus goes to the new page's heading
 * once it is drawn, unless the screen places the focus itself (`components/RouteFocus.tsx`).
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { RouteFocus } from './components/RouteFocus.tsx';
import {
  ACCOUNT_AGREEMENT_PATH,
  ACCOUNT_PATH,
  ACCOUNT_PAYING_PATH,
  ACCOUNT_PEOPLE_PATH,
  ACCOUNT_SENDING_PATH,
  FEEDBACK_ROUTE,
  HOME_PATH,
  INVOICE_ROUTE,
  MESSAGES_PATH,
  PAYMENT_RETURN_PATH,
  PAYMENTS_PATH,
  SCHEDULE_PATH,
  SIGN_IN_PATH,
  VISIT_ROUTE,
} from './paths.ts';
import { Account } from './screens/Account.tsx';
import { AccountAgreement } from './screens/AccountAgreement.tsx';
import { AccountPaying } from './screens/AccountPaying.tsx';
import { AccountPeople } from './screens/AccountPeople.tsx';
import { AccountSending } from './screens/AccountSending.tsx';
import { Entry } from './screens/Entry.tsx';
import { Feedback } from './screens/Feedback.tsx';
import { Home } from './screens/Home.tsx';
import { Invoice } from './screens/Invoice.tsx';
import { Messages } from './screens/Messages.tsx';
import { PaymentReturn } from './screens/PaymentReturn.tsx';
import { Payments } from './screens/Payments.tsx';
import { Schedule } from './screens/Schedule.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { VisitReport } from './screens/VisitReport.tsx';

export default function App() {
  return (
    <>
      <RouteFocus />
      <Routes>
        <Route path={HOME_PATH} element={<Entry screen={Home} />} />
        <Route path={SCHEDULE_PATH} element={<Entry screen={Schedule} />} />
        <Route path={PAYMENTS_PATH} element={<Entry screen={Payments} />} />
        <Route path={PAYMENT_RETURN_PATH} element={<PaymentReturn />} />
        <Route path={INVOICE_ROUTE} element={<Entry screen={Invoice} />} />
        <Route path={MESSAGES_PATH} element={<Entry screen={Messages} />} />
        <Route path={FEEDBACK_ROUTE} element={<Entry screen={Feedback} />} />
        <Route path={VISIT_ROUTE} element={<Entry screen={VisitReport} />} />
        <Route path={ACCOUNT_PATH} element={<Entry screen={Account} />} />
        <Route path={ACCOUNT_PAYING_PATH} element={<Entry screen={AccountPaying} />} />
        <Route path={ACCOUNT_PEOPLE_PATH} element={<Entry screen={AccountPeople} />} />
        <Route path={ACCOUNT_AGREEMENT_PATH} element={<Entry screen={AccountAgreement} />} />
        <Route path={ACCOUNT_SENDING_PATH} element={<Entry screen={AccountSending} />} />
        <Route path={SIGN_IN_PATH} element={<SignIn />} />
        <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
      </Routes>
    </>
  );
}
